import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FeedbackActions } from "@/features/grading/FeedbackActions";
import { ClassificationFilterDialog } from "@/features/reader/ClassificationFilterDialog";
import { useAppDispatch, useAppState } from "@/store/useApp";
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  NotepadText,
  PanelRightClose,
  SquareKanban,
  Text,
} from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router";
import OnboardingGenerateReportHint from "../reader/ onboarding/flows/OnboardingGenerateReportHint";
import OnboardingRubricViewHint from "../reader/ onboarding/flows/OnboardingRubricViewHint";
import { useApi } from "@/hooks/use-api";

export const Navbar = () => {
  const documents = useAppState((state) => state.documents || []);
  const currentDocument = useAppState((state) => state.currentDocument);
  const grading = useAppState((state) => state.grading);
  const referencePanelVisible = useAppState(
    (state) => state.referencePanel.visible
  );
  const gradingPanel = useAppState((state) => state.gradingPanel);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { documentId } = useParams();
  const { resetDocument } = useApi();

  const currentDocumentInfo = documents.find(
    (doc) => doc.id === currentDocument?.id
  );

  if (!currentDocument) return null;

  // Helper functions to find previous/next documents with same status
  const getPreviousDocumentWithSameStatus = () => {
    if (!currentDocument || !documents) return null;

    const currentIndex = documents.findIndex(
      (doc) => doc.id === currentDocument.id
    );
    if (currentIndex === -1) return null;

    // Look for the previous document with same status
    for (let i = currentIndex - 1; i >= 0; i--) {
      if (documents[i].status === currentDocument.status) {
        return documents[i];
      }
    }
    return null;
  };

  const getNextDocumentWithSameStatus = () => {
    if (!currentDocument || !documents) return null;

    const currentIndex = documents.findIndex(
      (doc) => doc.id === currentDocument.id
    );
    if (currentIndex === -1) return null;

    // Look for the next document with same status
    for (let i = currentIndex + 1; i < documents.length; i++) {
      if (documents[i].status === currentDocument.status) {
        return documents[i];
      }
    }
    return null;
  };

  const previousDocumentWithSameStatus = getPreviousDocumentWithSameStatus();
  const nextDocumentWithSameStatus = getNextDocumentWithSameStatus();

  // Determine current view from route
  const getCurrentView = () => {
    if (location.pathname.includes("/grading")) return "grading";
    if (location.pathname.includes("/rubric")) return "rubric";
    if (location.pathname.includes("/pdf")) return "pdf";
    return "reader";
  };

  const currentView = getCurrentView();

  const handleBack = async () => {
    if (currentView === "grading" || currentView === "pdf") {
      dispatch({ type: "SHOW_REFERENCE_PANEL", payload: { visible: false } });
      navigate(`/documents/${documentId}`);
    } else {
      // If navigating back to inbox from reader and document is inProgress, try to reset it
      if (currentDocument?.status === "in_progress" && documentId) {
        await resetDocument(documentId);
      }
      navigate("/");
    }
  };

  const handlePrevDocument = () => {
    if (previousDocumentWithSameStatus) {
      const prevDocId = previousDocumentWithSameStatus.id;
      // Navigate to the same view but with the new document
      const viewPath = currentView === "reader" ? "" : `/${currentView}`;
      navigate(`/documents/${prevDocId}${viewPath}`);
    }
  };

  const handleNextDocument = () => {
    if (nextDocumentWithSameStatus) {
      const nextDocId = nextDocumentWithSameStatus.id;
      // Navigate to the same view but with the new document
      const viewPath = currentView === "reader" ? "" : `/${currentView}`;
      navigate(`/documents/${nextDocId}${viewPath}`);
    }
  };

  const viewButton =
    currentView === "rubric" ? (
      <Button
        onClick={() => navigate(`/documents/${documentId}`)}
        variant="ghost"
        size="icon"
        aria-label="Show reader"
      >
        <Text className="h-6 w-6" />
      </Button>
    ) : (
      <>
        <OnboardingRubricViewHint />
        <Button
          onClick={() => navigate(`/documents/${documentId}/rubric`)}
          id="hint-anchor"
          variant="ghost"
          size="icon"
          aria-label="Show rubric board"
        >
          <SquareKanban className="h-6 w-6" />
        </Button>
      </>
    );

  const readerControls =
    currentView !== "grading" ? (
      <>
        {viewButton}
        <div className="flex items-center gap-2">
          <Button
            onClick={handlePrevDocument}
            variant="secondary"
            size="icon"
            disabled={!previousDocumentWithSameStatus}
            aria-label="Previous document"
          >
            <ChevronUp className="h-5 w-5" />
          </Button>
          <Button
            onClick={handleNextDocument}
            variant="secondary"
            size="icon"
            disabled={!nextDocumentWithSameStatus}
            aria-label="Next document"
          >
            <ChevronDown className="h-5 w-5" />
          </Button>
        </div>
      </>
    ) : null;

  return (
    <nav className="fixed top-0 left-0 right-0 flex items-center justify-between p-4 z-50">
      <>
        <div className="flex items-center gap-2">
          <Button
            aria-label="Navigate back"
            variant="ghost"
            size="icon"
            onClick={handleBack}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          {readerControls}
          <span className="text-xs ml-4 flex items-center gap-2">
            <span className="text-gray-500">Title</span>{" "}
            {currentDocumentInfo?.title || currentDocument?.studentId}
            {grading?.feedback?.status === "published" && (
              <Badge className="bg-green-500 text-white ml-2">Published</Badge>
            )}
            {currentDocument?.status === "flagged" && (
              <Badge className="bg-red-500 text-white ml-2">Flagged</Badge>
            )}
            {currentDocument?.validation &&
              !currentDocument.validation.passed &&
              currentView === "reader" && (
                <Badge
                  variant="secondary"
                  className="cursor-pointer"
                  onClick={() =>
                    dispatch({ type: "TOGGLE_DISCREPANCIES_PANEL" })
                  }
                >
                  Discrepancies
                </Badge>
              )}
          </span>
        </div>

        <div className="flex items-center gap-1">
          <>
            <FeedbackActions />
            {currentView === "reader" && <ClassificationFilterDialog />}
            <Button variant="ghost" size="icon">
              <a
                href="https://clairelabs.featurebase.app/help"
                target="_blank"
                rel="noopener noreferrer"
              >
                <HelpCircle className="h-6 w-6" />
              </a>
            </Button>
            <Button
              onClick={() => dispatch({ type: "TOGGLE_SCRATCHPAD" })}
              variant="ghost"
              aria-label="Toggle scratchpad"
              size="icon"
            >
              <NotepadText className="h-6 w-6" />
            </Button>

            {currentView === "rubric" && <OnboardingGenerateReportHint />}

            {currentView === "grading" && (
              <Button
                onClick={() =>
                  dispatch({
                    type: "SHOW_GRADING_PANEL",
                    payload: gradingPanel.visible
                      ? { visible: false }
                      : { visible: true, view: gradingPanel.view },
                  })
                }
                variant="ghost"
                size="icon"
              >
                <PanelRightClose className="h-6 w-6" />
              </Button>
            )}

            {currentView === "reader" && (
              <Button
                aria-label="Toggle reader panel"
                onClick={() =>
                  dispatch({
                    type: "SHOW_REFERENCE_PANEL",
                    payload: {
                      visible: !referencePanelVisible,
                      view: "references",
                    },
                  })
                }
                variant="ghost"
                size="icon"
              >
                <PanelRightClose className="h-6 w-6" />
              </Button>
            )}
          </>
        </div>
      </>
    </nav>
  );
};
